import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Box, Divider, Skeleton, Typography } from '@mui/material';

import DayNightShading from 'components/DayNightShading';
import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import useCustomError from 'hooks/useCustomError';
import type { SunlightTimes } from 'types/sunlightTimes';
import type { Weather } from 'types/weather';

import WeatherGraph from './WeatherGraph';

import styles from './ForecastGraphsWeatherContainer.module.scss';

export interface ForecastGraphsWeatherContainerProps {
  daysToDisplay: Array<number>;
  isMobile: boolean;
  loading?: boolean;
  sunlightTimes: SunlightTimes;
  weather: Weather;
}

const ForecastGraphsWeather = ({
  daysToDisplay,
  isMobile,
  loading,
  sunlightTimes,
  weather,
}: ForecastGraphsWeatherContainerProps) => {
  const weatherText = weather?.units?.temperature
    ? `Weather (º${weather.units.temperature.toLowerCase()})`
    : 'Weather';

  const items = useMemo(
    () =>
      daysToDisplay?.map((day: number, dayIndex) => {
        const data = weather?.days?.[day];
        return (
          <Box key={day} width="100%" display="flex">
            <Divider
              className={classNames(dayIndex !== 0 && styles.divider)}
              orientation="vertical"
              flexItem
              sx={{ border: 'none' }}
            />
            <div className={styles.dayWrapper} key={daysToDisplay[dayIndex]}>
              <Box className={styles.dayNightShading} data-testid="forecast-weather-inner">
                <DayNightShading
                  key={daysToDisplay[dayIndex]}
                  sunlightTimes={sunlightTimes}
                  numDays={daysToDisplay?.length}
                  forecastDayIndex={day}
                  columnIndex={dayIndex}
                  isPaywalled={false}
                  shadingOffset={0.03}
                />
                <WeatherGraph
                  data={data}
                  dateFormat="haaa"
                  isMobile={isMobile}
                  weatherIconPath={weather?.weatherIconPath}
                />
              </Box>
            </div>
          </Box>
        );
      }),
    [daysToDisplay, isMobile, sunlightTimes, weather],
  );

  return (
    <div className={styles.wrapper}>
      <Typography variant="callout1" component="h3" align="left">
        {weatherText}
      </Typography>
      {loading ? (
        <Skeleton
          data-testid="weather-loading"
          variant="rectangular"
          animation="wave"
          height={156}
          style={{ marginTop: 24 }}
          width="100%"
        />
      ) : (
        <div className={classNames([styles.content], 'sl-section-full-bleed')}>{items}</div>
      )}
    </div>
  );
};

const ForecastGraphsWeatherContainer = ({
  daysToDisplay,
  isMobile,
  loading,
  sunlightTimes,
  weather,
}: ForecastGraphsWeatherContainerProps) => {
  const { errorMessage, ErrorIcon, hasError } = useCustomError({ error: weather?.error });

  if (!sunlightTimes || !weather) return null;

  return (
    <div data-testid="weather">
      <ErrorBoundary
        error={hasError}
        errorBoundaryId="forecast-graphs-weather-container"
        render={() => (
          <Box data-testid="weather-error" style={{ position: 'relative', height: 100 }}>
            <ErrorMessage Icon={ErrorIcon} message={errorMessage} />
          </Box>
        )}
      >
        <ForecastGraphsWeather
          daysToDisplay={daysToDisplay}
          isMobile={isMobile}
          loading={loading}
          sunlightTimes={sunlightTimes}
          weather={weather}
        />
      </ErrorBoundary>
    </div>
  );
};
export default React.memo(ForecastGraphsWeatherContainer);
